// @flow
/**
 * HTTP client for the consumption microservice (direct, bypasses legacy API).
 * Auth: `authorization` header must match API_KEY (see microservices AuthGuard).
 */
import apisauce from 'apisauce';
import axios from 'axios';
// import config from 'config';
import { getCartridgeSpotId } from 'utils/cartridgeConsumption';

const toIsoParam = value => {
  if (value == null) return undefined;
  return value instanceof Date ? value.toISOString() : String(value);
};

const serializeQueryParams = params =>
  Object.entries(params).reduce((query, [key, value]) => {
    const serialized = toIsoParam(value);
    if (serialized !== undefined) {
      query[key] = serialized;
    }
    return query;
  }, {});

const RANGE_TO_INTERVAL = {
  hours: '1h',
  days: '1d',
  weeks: '1w',
  months: '1d',
  years: '1w',
};

const adaptConsumptionResponse = data => {
  if (!data || data.consumption) {
    return data;
  }

  const buckets = data.buckets ?? [];

  return {
    consumption: buckets.map(bucket => ({
      name: bucket.label,
      gal: bucket.gal,
      tooltipLabel: bucket.tooltipLabel,
      timestamp: bucket.timestamp,
      ...(bucket.inletFlow != null && { inletFlow: bucket.inletFlow }),
      ...(bucket.galInlet != null && { galInlet: bucket.galInlet }),
    })),
    total_flow: data.summary?.total ?? 0,
    permeate_flow: data.summary?.permeateFlow ?? null,
    inlet_flow: data.summary?.inletFlow ?? null,
    last_updated: data.summary?.lastUpdated ?? null,
  };
};

const adaptTotalConsumptionResponse = data => {
  if (!data) {
    return data;
  }

  const total = data.total ?? data.waterFilterTotalConsumption;

  return {
    ...data,
    total,
    waterFilterTotalConsumption: data.waterFilterTotalConsumption ?? total,
  };
};

const adaptBucketSeries = (buckets, valueKey) =>
  buckets.map(bucket => ({
    name: bucket.label,
    [valueKey]: bucket[valueKey],
    tooltipLabel: bucket.tooltipLabel,
    timestamp: bucket.timestamp,
  }));

const adaptMaxFlowRateResponse = data => {
  if (!data || data.consumption) {
    return data;
  }

  const buckets = data.buckets ?? [];

  return {
    consumption: adaptBucketSeries(buckets, 'gal'),
  };
};

const adaptPressureResponse = data => {
  if (!data || data.pressures) {
    return data;
  }

  const buckets = data.buckets ?? [];

  return {
    pressures: buckets.map(bucket => ({
      name: bucket.label,
      tooltipLabel: bucket.tooltipLabel,
      timestamp: bucket.timestamp,
      psi_0: bucket.psi_0 ?? bucket.pressure ?? 0,
      ...(bucket.psi_1 != null && { psi_1: bucket.psi_1 }),
      ...(bucket.psi_2 != null && { psi_2: bucket.psi_2 }),
    })),
  };
};

const buildTimeSeriesQuery = ({ range, interval, ...params }) => {
  const queryParams = { ...params };
  delete queryParams.wf_for_pfd;
  delete queryParams.ro_for_pfd;
  delete queryParams.device_id;
  delete queryParams.water_filter_head_id;
  delete queryParams.reverse_osmosis_filter_head_id;
  delete queryParams.softener_filter_head_id;
  delete queryParams.spot_id;
  delete queryParams.catridge_id;

  const resolvedInterval = interval || (range && RANGE_TO_INTERVAL[range]);

  return {
    ...queryParams,
    ...(resolvedInterval ? { interval: resolvedInterval } : {}),
  };
};

const normalizeSpotId = spotId => {
  if (spotId == null || spotId === '') {
    return undefined;
  }

  return String(spotId);
};

const adaptCartridgeConsumptionResponse = data => {
  if (!data) {
    return data;
  }

  const raw = data.cartridgeTotalConsumptionWf ?? data.total ?? 0;
  const numeric = typeof raw === 'number' ? raw : Number.parseFloat(raw);

  return {
    cartridgeTotalConsumptionWf: Number.isFinite(numeric) ? Math.round(numeric) : 0,
  };
};

const adaptCartridgeConsumptionRoResponse = data => {
  if (!data) {
    return data;
  }

  if (data.cartridgeTotalConsumptionRo != null) {
    return data;
  }

  const raw = data.total ?? 0;
  const numeric = typeof raw === 'number' ? raw : Number.parseFloat(raw);

  return {
    cartridgeTotalConsumptionRo: Number.isFinite(numeric) ? Math.round(numeric) : 0,
  };
};

const adaptTdsResponse = data => {
  if (!data || data.tds) {
    return data;
  }

  const buckets = data.buckets ?? [];

  return {
    tds: buckets.map(bucket => ({
      name: bucket.label,
      gal: bucket.tdsAvg,
      tooltipLabel: bucket.tooltipLabel,
      timestamp: bucket.timestamp,
    })),
    tdsLastHour: data.tdsLastHour ?? 0,
  };
};

const adaptWasteResponse = data => {
  if (!data || data.waste) {
    return data;
  }

  const buckets = data.buckets ?? [];

  return {
    waste: buckets.map(bucket => ({
      name: bucket.label,
      gal: bucket.wastePct,
      tooltipLabel: bucket.tooltipLabel,
      timestamp: bucket.timestamp,
    })),
  };
};

const parseNumericTotal = value => {
  if (value == null) {
    return 0;
  }

  const numeric = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const adaptTotalConsumptionRoResponse = data => {
  if (!data) {
    return data;
  }

  const nested = data.reverseOsmosisTotalConsumption;
  if (nested) {
    return {
      reverseOsmosisTotalConsumption: {
        reverseOsmosisTotalInletConsumption: parseNumericTotal(
          nested.reverseOsmosisTotalInletConsumption,
        ),
        reverseOsmosisTotalPermeateConsumption: parseNumericTotal(
          nested.reverseOsmosisTotalPermeateConsumption,
        ),
      },
    };
  }

  if (data.inletTotal != null || data.permeateTotal != null) {
    return {
      reverseOsmosisTotalConsumption: {
        reverseOsmosisTotalInletConsumption: parseNumericTotal(data.inletTotal),
        reverseOsmosisTotalPermeateConsumption: parseNumericTotal(data.permeateTotal),
      },
    };
  }

  return data;
};

const adaptTotalConsumptionSoftenerResponse = data => {
  if (!data) {
    return data;
  }

  const total = data.softenerTotalConsumption ?? data.total;

  return {
    ...data,
    softenerTotalConsumption: total,
    total,
  };
};

const adaptSoftenerCurrentStateResponse = data => {
  if (!data) {
    return { flow_2: null, distance: 0 };
  }

  return {
    ...data,
    distance: data.distance ?? data.distance_value ?? 0,
    flow_2: data.flow_2 ?? null,
    status_value: data.status_value ?? data.status ?? null,
  };
};

const adaptCurrentTemperatureResponse = data => {
  if (!data) {
    return data;
  }

  if (data.tempValue != null) {
    return data;
  }

  return {
    tempValue: data.temp ?? null,
  };
};

const parsePressureNumber = value => {
  if (value == null) {
    return null;
  }

  const numeric = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const adaptReverseOsmosisCurrentPressureResponse = data => {
  if (!data) {
    return data;
  }

  if (data.inletPressure != null || data.tankPressure != null || data.systemPressure != null) {
    return {
      inletPressure: parsePressureNumber(data.inletPressure),
      tankPressure: parsePressureNumber(data.tankPressure),
      systemPressure: parsePressureNumber(data.systemPressure),
    };
  }

  if (Array.isArray(data.pressures)) {
    const findRole = role => {
      const entry = data.pressures.find(pressure => pressure.label === role);
      return parsePressureNumber(entry?.value);
    };

    return {
      inletPressure: findRole('inlet'),
      tankPressure: findRole('tank'),
      systemPressure: findRole('system'),
    };
  }

  return data.pressure ?? data;
};

/** Legacy softener UI/reducer expect `{ pressure: { inletPressure, outletPressure } }`. */
const adaptSoftenerCurrentPressureResponse = data => {
  if (!data) {
    return data;
  }

  if (data.pressure?.inletPressure != null || data.pressure?.outletPressure != null) {
    return {
      pressure: {
        inletPressure: parsePressureNumber(data.pressure.inletPressure),
        outletPressure: parsePressureNumber(data.pressure.outletPressure),
      },
    };
  }

  if (data.inletPressure != null && data.outletPressure != null) {
    return {
      pressure: {
        inletPressure: parsePressureNumber(data.inletPressure),
        outletPressure: parsePressureNumber(data.outletPressure),
      },
    };
  }

  // Unified endpoint may return the RO-shaped payload; map tank → outlet for softener UI.
  if (data.inletPressure != null && data.tankPressure != null) {
    return {
      pressure: {
        inletPressure: parsePressureNumber(data.inletPressure),
        outletPressure: parsePressureNumber(data.tankPressure),
      },
    };
  }

  if (Array.isArray(data.pressures)) {
    const findRole = role => {
      const entry = data.pressures.find(pressure => pressure.label === role);
      return parsePressureNumber(entry?.value);
    };

    return {
      pressure: {
        inletPressure: findRole('inlet'),
        outletPressure: findRole('outlet') ?? findRole('tank'),
      },
    };
  }

  return data;
};

const missingSpotIdResponse = () => ({
  ok: false,
  status: 400,
  data: {
    errors: {
      message:
        'spotId is required for cartridge consumption (use spot_water_filter_head_id from the flow meter)',
    },
  },
});

const parseConsumptionTotal = data => {
  const raw = data?.waterFilterTotalConsumption ?? data?.total ?? 0;
  const numeric = typeof raw === 'number' ? raw : Number.parseFloat(raw);
  return Number.isFinite(numeric) ? numeric : 0;
};

const buildCartridgeSuccessResponse = total => ({
  ok: true,
  status: 200,
  data: adaptCartridgeConsumptionResponse({ total: String(total) }),
});

const adaptCurrentPressureResponse = data => {
  if (!data) {
    return data;
  }

  if (Array.isArray(data) && data.length > 0 && data[0]?.inletPressure != null) {
    return data.map(entry => ({
      inletPressure: parsePressureNumber(entry.inletPressure),
      outletPressure: parsePressureNumber(entry.outletPressure),
    }));
  }

  if (data.pressure?.inletPressure != null) {
    return [
      {
        inletPressure: parsePressureNumber(data.pressure.inletPressure),
        outletPressure: parsePressureNumber(data.pressure.outletPressure),
      },
    ];
  }

  if (Array.isArray(data.pressures)) {
    const inletOutlet = data.pressures.filter(
      pressure => pressure.label === 'inlet' || pressure.label === 'outlet',
    );
    const pairs = [];

    for (let index = 0; index < inletOutlet.length; index += 2) {
      const chunk = inletOutlet.slice(index, index + 2);
      const inlet = chunk.find(pressure => pressure.label === 'inlet');
      const outlet = chunk.find(pressure => pressure.label === 'outlet');

      if (inlet && outlet) {
        pairs.push({
          inletPressure: parsePressureNumber(inlet.value),
          outletPressure: parsePressureNumber(outlet.value),
        });
      }
    }

    return pairs;
  }

  return data;
};

const adaptCurrentBypassResponse = data => {
  if (!data || data.bypass !== undefined) {
    return data;
  }

  return { bypass: data };
};

const formatExportDateSlug = value => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'unknown';
  }

  return date
    .toISOString()
    .slice(0, 10)
    .replace(/-/g, '');
};

export const buildExportCsvFileName = ({
  device_id,
  location_id,
  from,
  to,
  mode = 'raw_data',
  prefix = 'water-filter',
}) => {
  const serial = String(device_id).replace(/:/g, '-');
  const fromSlug = formatExportDateSlug(from);
  const toSlug = formatExportDateSlug(to);
  const locationPart = location_id != null ? `${location_id}/` : '';

  return `${prefix}/${locationPart}${serial}_${fromSlug}_${toSlug}_${mode}`;
};

const create = (
  baseURL = process.env.REACT_APP_CONSUMPTION_BASE_URL,
  apiKey = process.env.REACT_APP_CONSUMPTION_API_KEY,
) => {
  const axiosInstance = axios.create({ baseURL });
  const api = apisauce.create({ axiosInstance });

  if (apiKey) {
    api.setHeader('authorization', apiKey);
  }

  api.addResponseTransform(response => {
    if (response.status === null || response.problem === 'NETWORK_ERROR') {
      response.data = {
        errors: { message: 'Unable to reach consumption service' },
      };
      response.status = 500;
      return;
    }

    if (response.status && response.status >= 400) {
      const { data } = response;
      const message =
        (data && typeof data.message === 'object' && data.message?.message) ||
        data?.message ||
        data?.errors?.message ||
        'Request failed';
      response.data = {
        errors: { message: typeof message === 'string' ? message : String(message) },
      };
    }
  });

  const getDeviceMetric = (serialNumber, segment, params = {}) =>
    api.get(`/device/${encodeURIComponent(serialNumber)}/${segment}`, serializeQueryParams(params));

  const getWaterFilterConsumption = async ({ device_id, range, interval, ...params }) => {
    const query = buildTimeSeriesQuery({ range, interval, ...params });
    const response = await getDeviceMetric(device_id, 'consumption', query);
    if (response.ok && response.data) {
      response.data = adaptConsumptionResponse(response.data);
    }
    return response;
  };

  const getWaterFilterMaxFlowRate = async ({ device_id, range, interval, ...params }) => {
    const query = buildTimeSeriesQuery({ range, interval, ...params });
    const response = await getDeviceMetric(device_id, 'max-flow-rate', query);
    if (response.ok && response.data) {
      response.data = adaptMaxFlowRateResponse(response.data);
    }
    return response;
  };

  const getWaterFilterPressure = async ({ device_id, range, interval, ...params }) => {
    const query = buildTimeSeriesQuery({ range, interval, ...params });
    const response = await getDeviceMetric(device_id, 'pressure', query);
    if (response.ok && response.data) {
      response.data = adaptPressureResponse(response.data);
    }
    return response;
  };

  const getWaterFilterBypass = async ({
    device_id,
    range,
    interval,
    installed_date,
    ...params
  }) => {
    const query = buildTimeSeriesQuery({ range, interval, installed_date, ...params });
    const response = await getDeviceMetric(device_id, 'bypass', query);
    if (response.ok && response.data) {
      response.data = adaptConsumptionResponse(response.data);
    }
    return response;
  };

  const getTotalConsumptionWaterFilter = async ({ device_id, ...params }) => {
    const response = await getDeviceMetric(device_id, 'total-consumption', params);
    if (response.ok && response.data) {
      response.data = adaptTotalConsumptionResponse(response.data);
    }
    return response;
  };

  const getTotalConsumptionCartridgeWf = async ({ device_id, from, to, spot_id, spotId }) => {
    const resolvedSpotId = normalizeSpotId(spotId ?? spot_id);
    if (!resolvedSpotId) {
      return missingSpotIdResponse();
    }

    const response = await getDeviceMetric(device_id, 'cartridge', {
      from,
      to,
      spotId: resolvedSpotId,
    });
    if (response.ok && response.data) {
      response.data = adaptCartridgeConsumptionResponse(response.data);
    }
    return response;
  };

  const sumPerSpotCartridgeConsumption = async (device_id, flowMeters, resolvedTo) => {
    const eligible = (flowMeters ?? []).filter(
      flowMeter => flowMeter.last_replaced && getCartridgeSpotId(flowMeter),
    );

    if (eligible.length === 0) {
      return { ok: true, total: 0 };
    }

    const responses = await Promise.all(
      eligible.map(flowMeter =>
        getTotalConsumptionCartridgeWf({
          device_id,
          from: flowMeter.last_replaced,
          to: resolvedTo,
          spotId: getCartridgeSpotId(flowMeter),
        }),
      ),
    );

    if (responses.some(response => !response.ok)) {
      return { ok: false, total: 0 };
    }

    const total = responses.reduce(
      (sum, response) => sum + (response.data?.cartridgeTotalConsumptionWf ?? 0),
      0,
    );

    return { ok: true, total };
  };

  const fallbackCartridgeConsumptionFromOutlet = async (device_id, flowMeters, resolvedTo) => {
    const anchorMeters = (flowMeters ?? []).filter(flowMeter => {
      if (!flowMeter.last_replaced) {
        return false;
      }

      return flowMeter.SpotsWaterFilter?.Catridge?.sediment !== true;
    });

    const meters =
      anchorMeters.length > 0 ? anchorMeters : (flowMeters ?? []).filter(fm => fm.last_replaced);
    if (meters.length === 0) {
      return 0;
    }

    const earliestFrom = meters
      .map(flowMeter => flowMeter.last_replaced)
      .sort((left, right) => new Date(left) - new Date(right))[0];

    const totalResponse = await getTotalConsumptionWaterFilter({
      device_id,
      from: earliestFrom,
      to: resolvedTo,
    });

    if (!totalResponse.ok || !totalResponse.data) {
      return 0;
    }

    return parseConsumptionTotal(totalResponse.data) / meters.length;
  };

  const getAllTotalConsumptionCartridgeWf = async ({ device_id, to, flowMeters }) => {
    const resolvedTo = to ?? new Date();

    if (Array.isArray(flowMeters) && flowMeters.length > 0) {
      const perSpot = await sumPerSpotCartridgeConsumption(device_id, flowMeters, resolvedTo);
      if (perSpot.ok && perSpot.total > 0) {
        return buildCartridgeSuccessResponse(perSpot.total);
      }

      const fallbackTotal = await fallbackCartridgeConsumptionFromOutlet(
        device_id,
        flowMeters,
        resolvedTo,
      );
      if (fallbackTotal > 0) {
        return buildCartridgeSuccessResponse(fallbackTotal);
      }
    }

    const response = await getDeviceMetric(device_id, 'cartridge/all', { to: resolvedTo });
    if (response.ok && response.data) {
      const adapted = adaptCartridgeConsumptionResponse(response.data);
      response.data = adapted;

      if (
        adapted.cartridgeTotalConsumptionWf === 0 &&
        Array.isArray(flowMeters) &&
        flowMeters.length > 0
      ) {
        const fallbackTotal = await fallbackCartridgeConsumptionFromOutlet(
          device_id,
          flowMeters,
          resolvedTo,
        );
        if (fallbackTotal > 0) {
          return buildCartridgeSuccessResponse(fallbackTotal);
        }
      }
    }

    return response;
  };

  const getWaterFilterCurrentState = async ({ device_id, from }) => {
    const response = await getDeviceMetric(device_id, 'state', { from });
    return response;
  };

  const getWaterFilterCurrentPressure = async ({ device_id }) => {
    const response = await getDeviceMetric(device_id, 'current');
    if (response.ok && response.data) {
      response.data = adaptCurrentPressureResponse(response.data);
    }
    return response;
  };

  const getWaterFilterCurrentBypass = async ({ device_id, from }) => {
    const response = await getDeviceMetric(device_id, 'current-bypass', { from });
    if (response.ok && response.data) {
      response.data = adaptCurrentBypassResponse(response.data);
    }
    return response;
  };

  const exportCsv = async ({ device_id, from, to, tz, email, fileName, mode, csvContains }) => {
    const response = await api.post(`/device/${encodeURIComponent(device_id)}/export-csv`, {
      from: toIsoParam(from),
      to: toIsoParam(to),
      tz,
      email,
      fileName,
      mode: mode ?? csvContains ?? 'raw_data',
    });

    if (response.status === 202 && !response.data) {
      response.data = {};
    }

    return response;
  };

  const getReverseOsmosisCurrentState = async ({ device_id }) =>
    getDeviceMetric(device_id, 'state');

  const getReverseOsmosisConsumption = async ({ device_id, range, interval, ...params }) => {
    const query = buildTimeSeriesQuery({ range, interval, ...params });
    const response = await getDeviceMetric(device_id, 'consumption', query);
    if (response.ok && response.data) {
      response.data = adaptConsumptionResponse(response.data);
    }
    return response;
  };

  const getReverseOsmosisTds = async ({ device_id, range, interval, now, ...params }) => {
    const query = buildTimeSeriesQuery({
      range,
      interval,
      now: now ?? new Date().toISOString(),
      ...params,
    });
    const response = await getDeviceMetric(device_id, 'tds', query);
    if (response.ok && response.data) {
      response.data = adaptTdsResponse(response.data);
    }
    return response;
  };

  const getReverseOsmosisWaste = async ({ device_id, range, interval, ...params }) => {
    const query = buildTimeSeriesQuery({ range, interval, ...params });
    const response = await getDeviceMetric(device_id, 'waste', query);
    if (response.ok && response.data) {
      response.data = adaptWasteResponse(response.data);
    }
    return response;
  };

  const getTotalConsumptionReverseOsmosis = async ({ device_id, ...params }) => {
    const response = await getDeviceMetric(device_id, 'total-consumption', params);
    if (response.ok && response.data) {
      response.data = adaptTotalConsumptionRoResponse(response.data);
    }
    return response;
  };

  const getTotalConsumptionCartridgeRo = async ({ device_id, from, to, spot_id, spotId }) => {
    const resolvedSpotId = normalizeSpotId(spotId ?? spot_id);
    if (!resolvedSpotId) {
      return {
        ok: false,
        status: 400,
        data: {
          errors: {
            message:
              'spotId is required for cartridge consumption (use spot_reverse_osmosis_filter_id from the flow meter)',
          },
        },
      };
    }

    const response = await getDeviceMetric(device_id, 'cartridge', {
      from,
      to,
      spotId: resolvedSpotId,
    });
    if (response.ok && response.data) {
      response.data = adaptCartridgeConsumptionRoResponse(response.data);
    }
    return response;
  };

  const getAllTotalConsumptionCartridgeRo = async ({ device_id, to }) => {
    const resolvedTo = to ?? new Date();
    const response = await getDeviceMetric(device_id, 'cartridge/all', { to: resolvedTo });
    if (response.ok && response.data) {
      response.data = adaptCartridgeConsumptionRoResponse(response.data);
    }
    return response;
  };

  const getReverseOsmosisBypass = async ({
    device_id,
    range,
    interval,
    installed_date,
    ...params
  }) => {
    const query = buildTimeSeriesQuery({ range, interval, installed_date, ...params });
    const response = await getDeviceMetric(device_id, 'bypass', query);
    if (response.ok && response.data) {
      response.data = adaptConsumptionResponse(response.data);
    }
    return response;
  };

  const getReverseOsmosisCurrentBypass = async ({ device_id, from }) => {
    const response = await getDeviceMetric(device_id, 'current-bypass', {
      from: from ?? new Date(),
    });
    if (response.ok && response.data) {
      response.data = adaptCurrentBypassResponse(response.data);
    }
    return response;
  };

  const getReverseOsmosisCurrentPressure = async ({ device_id }) => {
    const response = await getDeviceMetric(device_id, 'current');
    if (response.ok && response.data) {
      response.data = adaptReverseOsmosisCurrentPressureResponse(response.data);
    }
    return response;
  };

  const getReverseOsmosisCurrentTemperature = async ({ device_id }) => {
    const response = await getDeviceMetric(device_id, 'current');
    if (response.ok && response.data) {
      response.data = adaptCurrentTemperatureResponse(response.data);
    }
    return response;
  };

  const getSoftenerCurrentState = async ({ device_id, from }) => {
    const response = await getDeviceMetric(device_id, 'state', { from });
    if (response.ok && response.data) {
      response.data = adaptSoftenerCurrentStateResponse(response.data);
    }
    return response;
  };

  const getSoftenerConsumption = async ({ device_id, range, interval, ...params }) => {
    const query = buildTimeSeriesQuery({ range, interval, ...params });
    const response = await getDeviceMetric(device_id, 'consumption', query);
    if (response.ok && response.data) {
      response.data = adaptConsumptionResponse(response.data);
    }
    return response;
  };

  const getSoftenerPressure = async ({ device_id, range, interval, ...params }) => {
    const query = buildTimeSeriesQuery({ range, interval, ...params });
    const response = await getDeviceMetric(device_id, 'pressure', query);
    if (response.ok && response.data) {
      response.data = adaptPressureResponse(response.data);
    }
    return response;
  };

  const getSoftenerBypass = async ({ device_id, range, interval, installed_date, ...params }) => {
    const query = buildTimeSeriesQuery({ range, interval, installed_date, ...params });
    const response = await getDeviceMetric(device_id, 'bypass', query);
    if (response.ok && response.data) {
      response.data = adaptConsumptionResponse(response.data);
    }
    return response;
  };

  const getSoftenerWaste = async ({ device_id, range, interval, ...params }) => {
    const query = buildTimeSeriesQuery({ range, interval, ...params });
    const response = await getDeviceMetric(device_id, 'waste', query);
    if (response.ok && response.data) {
      response.data = adaptWasteResponse(response.data);
    }
    return response;
  };

  const getTotalConsumptionSoftener = async ({ device_id, ...params }) => {
    const response = await getDeviceMetric(device_id, 'total-consumption', params);
    if (response.ok && response.data) {
      response.data = adaptTotalConsumptionSoftenerResponse(response.data);
    }
    return response;
  };

  const getSoftenerCurrentPressure = async ({ device_id, from }) => {
    const response = await getDeviceMetric(device_id, 'current-pressure', { from });
    if (response.ok && response.data) {
      response.data = adaptSoftenerCurrentPressureResponse(response.data);
    }
    return response;
  };

  const getSoftenerCurrentBypass = async ({ device_id, from }) => {
    const response = await getDeviceMetric(device_id, 'current-bypass', {
      from: from ?? new Date(),
    });
    if (response.ok && response.data) {
      response.data = adaptCurrentBypassResponse(response.data);
    }
    return response;
  };

  return {
    getWaterFilterConsumption,
    getWaterFilterMaxFlowRate,
    getWaterFilterPressure,
    getWaterFilterBypass,
    getTotalConsumptionWaterFilter,
    getTotalConsumptionCartridgeWf,
    getAllTotalConsumptionCartridgeWf,
    getWaterFilterCurrentState,
    getWaterFilterCurrentPressure,
    getWaterFilterCurrentBypass,
    getReverseOsmosisCurrentState,
    getReverseOsmosisConsumption,
    getReverseOsmosisTds,
    getReverseOsmosisWaste,
    getTotalConsumptionReverseOsmosis,
    getTotalConsumptionCartridgeRo,
    getAllTotalConsumptionCartridgeRo,
    getReverseOsmosisBypass,
    getReverseOsmosisCurrentBypass,
    getReverseOsmosisCurrentPressure,
    getReverseOsmosisCurrentTemperature,
    getSoftenerCurrentState,
    getSoftenerConsumption,
    getSoftenerPressure,
    getSoftenerBypass,
    getSoftenerWaste,
    getTotalConsumptionSoftener,
    getSoftenerCurrentPressure,
    getSoftenerCurrentBypass,
    exportCsv,
  };
};

export default {
  create,
};
